import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import { KeylistenerContext, useKeyListener } from "./keylistener-context";
import { TimelineContext, useTimelineSetFrame, usePlayingState } from "./timeline-position-state";
import { useProject } from '../store/projectContext';
import { PlayIcon, PauseIcon } from './shared/Icons';
import { getTotalDuration } from '../utils/sceneCalculator';

const ControlsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
`;

const PlayButton = styled.button`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const FrameInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 120px;
`;

const FrameNumber = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: #2d3748;
`;

const FrameLabel = styled.div`
    font-size: 12px;
    color: #718096;
    font-weight: 500;
`;

const Divider = styled.div`
    width: 1px;
    height: 32px;
    background: #e2e8f0;
`;

const useKeyPress = (targetKey) => {
    const [keyPressedVal, setKeyPressed] = useState(null);
    const downHandler = ({ key }) => {
        setKeyPressed(key);
    };

    const upHandler = ({ key }) => {
        setKeyPressed(null);
    };

    useEffect(() => {
        window.addEventListener("keydown", downHandler);
        window.addEventListener("keyup", upHandler);

        return () => {
            window.removeEventListener("keydown", downHandler);
            window.removeEventListener("keyup", upHandler);
        };
    }, []);

    return { keyPressedVal };
};

const calculateNextFrame = ({
    time,
    currentFrame: startFrame,
    playbackRate,
    fps,
    actualLastFrame,
    actualFirstFrame,
    framesAdvanced,
    shouldLoop,
}) => {
    const op = playbackRate < 0 ? Math.ceil : Math.floor;
    const framesToAdvance =
        op((time * playbackRate) / (1000 / fps)) - framesAdvanced;

    const nextFrame = framesToAdvance + startFrame;
    const isCurrentFrameOutside =
        startFrame > actualLastFrame || startFrame < actualFirstFrame;
    const isNextFrameOutside =
        nextFrame > actualLastFrame || nextFrame < actualFirstFrame;

    const hasEnded =
        !shouldLoop && isNextFrameOutside && !isCurrentFrameOutside;
    if (playbackRate > 0) {
        if (isNextFrameOutside) {
            return {
                nextFrame: actualFirstFrame,
                framesToAdvance,
                hasEnded,
            };
        }

        return { nextFrame, framesToAdvance, hasEnded };
    }

    if (isNextFrameOutside) {
        return { nextFrame: actualLastFrame, framesToAdvance, hasEnded };
    }

    return { nextFrame, framesToAdvance, hasEnded };
};

const Player = (props) => {
    const { setKeyPressed, keyPressed } = useContext(KeylistenerContext);
    const {
        frame,
        playbackRate,
        loop,
        reverse,
        buffering,
    } = useContext(TimelineContext);
    const setFrame = useTimelineSetFrame();
    const [playing, setPlaying] = usePlayingState()
    const { keyPressedVal } = useKeyPress();
    const intervalRef = useRef(null);
    const frameRef = useRef(frame);
    frameRef.current = frame;
    const bufferingRef = useRef(buffering);
    bufferingRef.current = buffering;
    const moveToBeginningWhenEnded = true;

    // Always call hook unconditionally - returns null if not in ProjectProvider
    const project = useProject();

    const fps = project?.fps || props.videoConfig?.fps || 30;

    const totalFrames = project?.scenes?.length
        ? getTotalDuration(project.scenes, fps)
        : (props.videoConfig?.fps || 30) * (props.videoConfig?.duration || 5);

    useEffect(() => {
        if (keyPressedVal === " ") {
            const tag = document.activeElement?.tagName;
            const isEditable = document.activeElement?.contentEditable === 'true';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable) return;
            setPlaying(!playing);
        }
    }, [keyPressedVal]);

    useEffect(() => {
        if (!playing) {
            return;
        }
        let hasBeenStopped = false;
        let reqAnimFrameCall = { id: null };
        let startedTime = performance.now();
        let lastFrameTime = performance.now();
        let framesAdvanced = 0;

        const cancelQueuedFrame = () => {
            cancelAnimationFrame(reqAnimFrameCall.id);
        };

        const stop = () => {
            hasBeenStopped = true;
            setPlaying(false);
            cancelQueuedFrame();
        };

        const callback = () => {
            const now = performance.now();
            if (bufferingRef.current) {
                // If buffering, offset startedTime by time spent buffering in this tick
                startedTime += (now - lastFrameTime);
                lastFrameTime = now;
                if (!hasBeenStopped) {
                    queueNextFrame();
                }
                return;
            }
            lastFrameTime = now;

            const time = now - startedTime;
            const actualLastFrame = totalFrames - 1;
            const actualFirstFrame = 0;

            const { nextFrame, framesToAdvance, hasEnded } = calculateNextFrame(
                {
                    time,
                    currentFrame: frameRef.current,
                    playbackRate: !reverse ? playbackRate : playbackRate * -1,
                    fps: fps,
                    actualFirstFrame,
                    actualLastFrame,
                    framesAdvanced,
                    shouldLoop: loop,
                }
            );
            framesAdvanced += framesToAdvance;

            if (
                nextFrame !== frameRef.current &&
                (!hasEnded || moveToBeginningWhenEnded)
            ) {
                setFrame(nextFrame);
            }

            if (hasEnded) {
                stop();
                return;
            }

            if (!hasBeenStopped) {
                queueNextFrame();
            }
        };

        const queueNextFrame = () => {
            reqAnimFrameCall = { id: requestAnimationFrame(callback) };
        };

        queueNextFrame();

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                return;
            }
            cancelQueuedFrame();
            callback();
        };

        window.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.removeEventListener("visibilitychange", onVisibilityChange);
            stop();
        };
    }, [playing, setFrame, loop, playbackRate, reverse, totalFrames, project]);

    return (
        <ControlsContainer>
            <PlayButton onClick={() => setPlaying(!playing)}>
                {playing ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
            </PlayButton>
            <FrameInfo>
                <FrameNumber>{frame} / {totalFrames - 1}</FrameNumber>
                <FrameLabel>Frame</FrameLabel>
            </FrameInfo>
            <Divider />
            <FrameInfo>
                <FrameNumber>{(frame / fps).toFixed(2)}s</FrameNumber>
                <FrameLabel>Time</FrameLabel>
            </FrameInfo>
        </ControlsContainer>
    );
};

export default React.memo(Player);
